import {priority} from '@priority-software/priority-proto-api/lib';
import {
    Button,
    CheckBox,
    ChooseDropdown,
    DropdownSelectTrigger,
    Flex,
    Icon,
    Input,
    PriorityIcon,
    Scrollbar,
    Text
} from '@priority-software/priority-style-react';
import {observer} from 'mobx-react-lite';
import React, {useCallback, useMemo} from 'react';
import classNames from 'classnames';

import DraggableList, {InjectedDraggableProps} from '../../../../components/Common/DraggableList';
import {
    MARGIN_OFFSET_TO_BOTTOM,
    buildTableHeaderCols,
    functionsOptionsExpressionsDialog
} from './consts';
import styles from './styles.module.scss';
import {
    buildFunctionsOptions,
    buildGroupFunctionsOptions,
    buildGroupTitleOptions,
    buildGroupOptions,
    buildSortOptions
} from './utils';
import {isRTL} from '../../../../Localization';
import {reportGeneratorsStore} from '../../stores/ReportGeneratorsStore';
import texts from '../../texts';
import HeaderItem from './HeaderItem';
import {useQuery} from 'react-query';
import {ColumnsFieldsNames, ReportColumn} from '../../types';
import {createDataTestId} from '../../../../utils/dataTestId';
import {ModernTooltip} from '../../../../components/Common/Tooltip/ModernTooltip';
import {AutomationFunctionComponent} from '../../../Automations/components/FunctionDialog/Dialog';

import {
    TabsDataItem,
    tabsDataFunctionDialog
} from '../../../Automations/components/FunctionDialog/constants';
import {getNumericFormats} from '../../services/reports';
import {getDateFormats} from '../../services/service';
import {
    handleDeleteCompanyCol,
    toggleHiddenCol,
    handleColTitleUpdate,
    handleDeletCol,
    handleDateFormatUpdate,
    handleNumericFormatUpdate,
    handleGroupUpdate
} from '../../utils';
import {useScrollWrapDimensions} from '../../../../hooks/customScroll/useScrollWrapDimensions';

type FieldTableProps = {
    isDataChart?: boolean;
};

const FieldTable = observer(({isDataChart}: FieldTableProps) => {
    const {currentScreen} = reportGeneratorsStore;
    const {currentReport} = currentScreen;
    const {
        updateColumn,
        deleteColumn,
        columns,
        isTabular,
        aXisModeData,
        reorderColumns,
        openFunctionDialog,
        setOpenFunctionDialog
    } = currentReport;

    const {data: formatOptions} = useQuery('formatFormats', getNumericFormats, {
        staleTime: Infinity,
        select: data => {
            const numFormats = data?.Formats?.map(format => ({
                value: format.Id,
                label: format.Code,
                description: format?.Name,
                ...format
            })).sort((a, b) => a.Code.localeCompare(b.Code));

            return numFormats
                ? [{value: 0, label: '--', description: ''}, ...numFormats]
                : numFormats;
        }
    });
    const {data: dateOptions} = useQuery('dateFormats', getDateFormats, {
        staleTime: Infinity,
        select: data => {
            const serverData = data.map(format => ({value: format.Id, label: format.Pattern}));
            return [{value: 0, label: '--'}, ...serverData];
        }
    });

    const {setWrapRef, maxHeight: scrollMaxHeight} = useScrollWrapDimensions();

    const functionOptionsData: TabsDataItem[] = useMemo(() => {
        if (openFunctionDialog?.isOpen) {
            const fields = columns
                .filter(item => {
                    return item.ColId && item.ColId !== openFunctionDialog.column.ColId;
                })
                .map(item => {
                    const fieldName = item.Expr
                        ? item.Title
                        : item.NodeId?.split(':')[1] || item.OrigTitle;
                    return {
                        value: item.OrigTitle,
                        label: item.Title,
                        description: fieldName,
                        data: {Title: fieldName, id: item.ColId}
                    };
                });

            const functions = functionsOptionsExpressionsDialog().map(item => ({
                value: item.title,
                label: item.title,
                description: item.description,
                data: item
            }));

            return tabsDataFunctionDialog(fields, functions);
        }
        return [];
    }, [columns.length, openFunctionDialog.isOpen]);

    const headerColumns = buildTableHeaderCols({isDataChart});
    const groupTitleOptions = buildGroupTitleOptions();
    const functionOptions = buildFunctionsOptions();
    const groupFunctionOptions = buildGroupFunctionsOptions(currentReport.columns);
    const groupOptions = buildGroupOptions(currentReport.columns);
    const sortOptions = buildSortOptions(currentReport.columns);
    const rtl = isRTL();

    const handleDelete = index => {
        const col = currentReport.columns[index];
        if (col.IsCompany) {
            handleDeleteCompanyCol();
        } else {
            handleDeletCol(Number(col.ColId));
        }
        deleteColumn(index);
    };

    const onToggleHidden = (index, item) => {
        updateColumn(index, ColumnsFieldsNames.Hide, !item.Hide);
        const column = columns[index];
        if (isDataChart && column[ColumnsFieldsNames.Hide]) {
            toggleHiddenCol(Number(column.ColId));
        }
    };

    const onToggleInput = (index, item) => {
        updateColumn(index, ColumnsFieldsNames.Input, !item.Input);
        reportGeneratorsStore?.currentScreen?.resetExplantoryReport();
    };

    const onChangeColumnTitle = (index: number, value: string) => {
        updateColumn(index, ColumnsFieldsNames.Title, value);
        const column = columns[index];
        handleColTitleUpdate(Number(column.ColId));
    };

    const onChangeDateFormat = (index: number, value: number) => {
        updateColumn(index, ColumnsFieldsNames.DateFormat, value);
        const column = columns[index];
        handleDateFormatUpdate(Number(column.ColId), value);
    };

    const onChangeNumericFormat = (index: number, value: number) => {
        updateColumn(index, ColumnsFieldsNames.NumericFormat, value);
        const column = columns[index];
        handleNumericFormatUpdate(Number(column.ColId), value);
    };

    const onGroupUpdate = (index: number, value: number) => {
        updateColumn(index, ColumnsFieldsNames.Group, value);
        if (!value) {
            updateColumn(
                index,
                ColumnsFieldsNames.Header,
                priority.netitems.nrest.RepColHeaderModes.RepColHeaderModes_None
            );
        }
        const column = columns[index];
        handleGroupUpdate(Number(column.ColId), value);
    };

    const onSortUpdate = (index: number, value: number) => {
        if (value > 0) {
            updateColumn(index, ColumnsFieldsNames.Sort, value);
            updateColumn(
                index,
                ColumnsFieldsNames.SortType,
                priority.netitems.nrest.RepColSortType.RepColSortType_Ascending
            );
        } else {
            updateColumn(index, ColumnsFieldsNames.Sort, 0);
            updateColumn(
                index,
                ColumnsFieldsNames.SortType,
                priority.netitems.nrest.RepColSortType.RepColSortType_None
            );
        }
    };

    const handleAddFunction = (column: ReportColumn | null, index: number) => {
        setOpenFunctionDialog({index, column, isOpen: true});
    };

    const closeFunctionDialog = useCallback(() => {
        setOpenFunctionDialog({index: null, column: null, isOpen: false});
    }, []);

    const onFunctionDialogSave = useCallback(
        async (value: string) => {
            updateColumn(openFunctionDialog.index, ColumnsFieldsNames.Expr, value);
            closeFunctionDialog();
        },
        [openFunctionDialog.index]
    );

    const onCriticalError = useCallback(async () => {
        closeFunctionDialog();
    }, []);

    const getSortIcon = (column: ReportColumn): PriorityIcon => {
        const sortTypeNone: boolean =
            column.SortType === priority.netitems.nrest.RepColSortType.RepColSortType_None;
        if (column.Sort && sortTypeNone) {
            return PriorityIcon.sorted;
        } else if (!column.Sort || sortTypeNone) {
            return PriorityIcon.sort;
        } else {
            return PriorityIcon.sorted;
        }
    };

    return (
        <>
            <div className={styles.wrapper} ref={setWrapRef}>
                {currentReport?.columns?.length > 0 && (
                    <Scrollbar
                        rtl={rtl}
                        autoHide={false}
                        autoHeight
                        autoHeightMax={scrollMaxHeight}
                        componentClasses={styles.tableScroll}
                    >
                        <div
                            className={styles.tableContainer}
                            {...createDataTestId('new-report-table-container')}
                        >
                            <div className={styles.table}>
                                <div
                                    className={classNames(styles.tableHeader, {
                                        [styles.dataChart]: isDataChart
                                    })}
                                    {...createDataTestId('new-report-table-header')}
                                >
                                    <div className={styles.tableHeaderItem}></div>
                                    {headerColumns.map((column, index) => (
                                        <HeaderItem
                                            key={`header_${index}`}
                                            column={column}
                                            index={index}
                                        />
                                    ))}
                                    <div className={styles.tableHeaderItem}></div>
                                </div>

                                <DraggableList
                                    handleDNDFinish={reorderColumns}
                                    componentClasses={styles.tableBody}
                                    data={columns as ReportColumn[]}
                                    renderItem={({item, index, ...props}) => {
                                        const draggableItemProps = props as InjectedDraggableProps;

                                        const isDate =
                                            item.Type === priority.netitems.form.ColumnType.Date8 ||
                                            item.Type ===
                                                priority.netitems.form.ColumnType.Date10 ||
                                            item.Type === priority.netitems.form.ColumnType.Date14;

                                        const isNumeric =
                                            !item.IsCompany &&
                                            (item.Type === priority.netitems.form.ColumnType.Int ||
                                                item.Type ===
                                                    priority.netitems.form.ColumnType.Real);
                                        const numericFormatItem = formatOptions?.find(
                                            option => option.Id === item.NumericFormat
                                        );
                                        return (
                                            <div
                                                ref={draggableItemProps.innerRef}
                                                {...draggableItemProps.draggableProps}
                                                key={item.ColId || item.OrigTitle}
                                                className={classNames(styles.tableRow, {
                                                    [styles.draggedItem]:
                                                        draggableItemProps.isDragged,
                                                    [styles.dataChart]: isDataChart
                                                })}
                                                {...createDataTestId('new-report-table-row')}
                                            >
                                                <div
                                                    className={styles.handle}
                                                    {...draggableItemProps.dragHandleProps}
                                                    {...createDataTestId(
                                                        'new-report-list-item-draghandle'
                                                    )}
                                                >
                                                    <Icon icon={PriorityIcon.drag} />
                                                </div>
                                                <Flex
                                                    justifyContent="center"
                                                    componentClasses={classNames(
                                                        styles.tableItem,
                                                        styles.expressionField
                                                    )}
                                                    {...createDataTestId(
                                                        'new-report-list-item-newtitle',
                                                        true
                                                    )}
                                                >
                                                    <ModernTooltip
                                                        wrapperClassname={styles.numericTooltip}
                                                        className={classNames(styles.exprTooltip, {
                                                            [styles.displayNone]: !item.FormTitle
                                                        })}
                                                        tooltip={item.FormTitle}
                                                        position="bottom"
                                                    >
                                                        <Input
                                                            direction={rtl ? 'rtl' : 'ltr'}
                                                            value={item.Title}
                                                            maxLength={20}
                                                            onChange={e =>
                                                                onChangeColumnTitle(
                                                                    index,
                                                                    e.target.value
                                                                )
                                                            }
                                                        >
                                                            {!item.IsExpression && (
                                                                <ModernTooltip
                                                                    tooltip={
                                                                        <Text>
                                                                            {texts.revertTooltip}
                                                                            &nbsp;
                                                                            <strong>
                                                                                {item.OrigTitle}
                                                                            </strong>
                                                                        </Text>
                                                                    }
                                                                    position="top"
                                                                >
                                                                    <Icon
                                                                        icon={PriorityIcon.refresh}
                                                                        componentClasses={
                                                                            styles.refreshIcon
                                                                        }
                                                                        onClick={() =>
                                                                            onChangeColumnTitle(
                                                                                index,
                                                                                item.OrigTitle
                                                                            )
                                                                        }
                                                                    />
                                                                </ModernTooltip>
                                                            )}
                                                        </Input>
                                                    </ModernTooltip>
                                                    {item.IsExpression && (
                                                        <ModernTooltip
                                                            className={styles.exprTooltip}
                                                            tooltip={`${
                                                                item.Expr || texts.addExpression
                                                            }`}
                                                            position="top"
                                                        >
                                                            <Button
                                                                skin="secondary"
                                                                componentClasses={classNames(
                                                                    styles.funcButton,
                                                                    {
                                                                        [styles.fullExpressions]:
                                                                            item.Expr,
                                                                        [styles.emptyExpressions]:
                                                                            item.error
                                                                    }
                                                                )}
                                                                onClick={() =>
                                                                    handleAddFunction(item, index)
                                                                }
                                                            >
                                                                <Icon
                                                                    icon={PriorityIcon.function}
                                                                    componentClasses={
                                                                        styles.funcIcon
                                                                    }
                                                                />
                                                            </Button>
                                                        </ModernTooltip>
                                                    )}
                                                </Flex>
                                                <Flex
                                                    justifyContent="center"
                                                    componentClasses={styles.tableItem}
                                                    {...createDataTestId(
                                                        'new-report-list-item-input',
                                                        true
                                                    )}
                                                >
                                                    <CheckBox
                                                        componentClasses={styles.checkBox}
                                                        checked={item.Input}
                                                        disabled={item.IsExpression}
                                                        onChange={() => onToggleInput(index, item)}
                                                    >
                                                        {item.Input}
                                                    </CheckBox>
                                                </Flex>
                                                <Flex
                                                    justifyContent="center"
                                                    componentClasses={classNames(
                                                        styles.tableItem,
                                                        styles.separator
                                                    )}
                                                    {...createDataTestId(
                                                        'new-report-list-item-sort',
                                                        true
                                                    )}
                                                >
                                                    <SelectItem
                                                        options={sortOptions}
                                                        value={Number(item.Sort) || 0}
                                                        onSelect={item =>
                                                            onSortUpdate(index, item.value)
                                                        }
                                                    />
                                                    <ModernTooltip
                                                        tooltip={texts.sortDirection}
                                                        position="top"
                                                    >
                                                        <Icon
                                                            disabled={!item.Sort}
                                                            icon={getSortIcon(item)}
                                                            componentClasses={classNames({
                                                                [styles.sortIcon]:
                                                                    item.SortType ===
                                                                    priority.netitems.nrest
                                                                        .RepColSortType
                                                                        .RepColSortType_Descending
                                                            })}
                                                            onClick={() =>
                                                                updateColumn(
                                                                    index,
                                                                    ColumnsFieldsNames?.SortType,
                                                                    item?.SortType ===
                                                                        priority.netitems.nrest
                                                                            .RepColSortType
                                                                            .RepColSortType_Ascending ||
                                                                        item.SortType ===
                                                                            priority.netitems.nrest
                                                                                .RepColSortType
                                                                                .RepColSortType_None
                                                                        ? priority.netitems.nrest
                                                                              .RepColSortType
                                                                              .RepColSortType_Descending
                                                                        : priority.netitems.nrest
                                                                              .RepColSortType
                                                                              .RepColSortType_Ascending
                                                                )
                                                            }
                                                        />
                                                    </ModernTooltip>
                                                </Flex>

                                                <Flex
                                                    justifyContent="center"
                                                    componentClasses={styles.tableItem}
                                                    {...createDataTestId(
                                                        'new-report-list-item-group',
                                                        true
                                                    )}
                                                >
                                                    <SelectItem
                                                        options={groupOptions}
                                                        value={item.Group || 0}
                                                        onSelect={item =>
                                                            onGroupUpdate(index, item.value)
                                                        }
                                                    />
                                                </Flex>
                                                {isDataChart ? null : (
                                                    <Flex
                                                        justifyContent={
                                                            item.Group ? 'center' : 'flex-start'
                                                        }
                                                        componentClasses={styles.tableItem}
                                                        {...createDataTestId(
                                                            'new-report-list-item-groupintitle',
                                                            true
                                                        )}
                                                    >
                                                        {item.Group ? (
                                                            <SelectItem
                                                                options={groupTitleOptions}
                                                                value={
                                                                    item.Header ||
                                                                    priority.netitems.nrest
                                                                        .RepColHeaderModes
                                                                        .RepColHeaderModes_None
                                                                }
                                                                onSelect={item =>
                                                                    updateColumn(
                                                                        index,
                                                                        ColumnsFieldsNames.Header,
                                                                        item.value
                                                                    )
                                                                }
                                                            />
                                                        ) : (
                                                            <Text>-</Text>
                                                        )}
                                                    </Flex>
                                                )}

                                                <Flex
                                                    justifyContent={
                                                        isNumeric ? 'center' : 'flex-start'
                                                    }
                                                    componentClasses={styles.tableItem}
                                                    {...createDataTestId(
                                                        'new-report-list-item-function',
                                                        true
                                                    )}
                                                >
                                                    {isNumeric ? (
                                                        <SelectItem
                                                            options={functionOptions}
                                                            value={item.Func}
                                                            onSelect={item =>
                                                                updateColumn(
                                                                    index,
                                                                    ColumnsFieldsNames.Func,
                                                                    item.value
                                                                )
                                                            }
                                                        />
                                                    ) : (
                                                        <Text>-</Text>
                                                    )}
                                                </Flex>
                                                {isDataChart ? null : (
                                                    <Flex
                                                        justifyContent={
                                                            isNumeric ? 'center' : 'flex-start'
                                                        }
                                                        componentClasses={styles.tableItem}
                                                        {...createDataTestId(
                                                            'new-report-list-item-group-function',
                                                            true
                                                        )}
                                                    >
                                                        {isNumeric ? (
                                                            <SelectItem
                                                                options={groupFunctionOptions}
                                                                value={item.GroupFunc}
                                                                onSelect={item =>
                                                                    updateColumn(
                                                                        index,
                                                                        ColumnsFieldsNames.GroupFunc,
                                                                        item.value
                                                                    )
                                                                }
                                                            />
                                                        ) : (
                                                            <Text>-</Text>
                                                        )}
                                                    </Flex>
                                                )}
                                                <Flex
                                                    justifyContent={
                                                        isNumeric ? 'center' : 'flex-start'
                                                    }
                                                    componentClasses={classNames(
                                                        styles.tableItem,
                                                        styles.separator
                                                    )}
                                                    {...createDataTestId(
                                                        'new-report-list-item-format',
                                                        true
                                                    )}
                                                >
                                                    {isNumeric ? (
                                                        <ModernTooltip
                                                            {...createDataTestId(
                                                                'new-report-list-item-format-tooltip',
                                                                true
                                                            )}
                                                            className={classNames({
                                                                [styles.displayNone]:
                                                                    !numericFormatItem?.Name
                                                            })}
                                                            wrapperClassname={styles.numericTooltip}
                                                            tooltip={numericFormatItem?.Name}
                                                            position={
                                                                isRTL() ? 'top-start' : 'top-end'
                                                            }
                                                        >
                                                            <SelectItem
                                                                options={formatOptions || []}
                                                                value={item.NumericFormat}
                                                                onSelect={val => {
                                                                    onChangeNumericFormat(
                                                                        index,
                                                                        val.value
                                                                    );
                                                                }}
                                                            />
                                                        </ModernTooltip>
                                                    ) : isDate ? (
                                                        <SelectItem
                                                            options={dateOptions || []}
                                                            value={item.DateFormat}
                                                            onSelect={val =>
                                                                onChangeDateFormat(index, val.value)
                                                            }
                                                        />
                                                    ) : (
                                                        <Text>-</Text>
                                                    )}
                                                </Flex>
                                                <Flex
                                                    justifyContent="center"
                                                    componentClasses={styles.tableItem}
                                                    {...createDataTestId(
                                                        'new-report-list-item-width',
                                                        true
                                                    )}
                                                >
                                                    <Input
                                                        maxLength={3}
                                                        type="number"
                                                        state={
                                                            item.error?.includes(
                                                                ColumnsFieldsNames.Width
                                                            )
                                                                ? 'error'
                                                                : null
                                                        }
                                                        value={item.Width?.toString()}
                                                        onChange={e =>
                                                            updateColumn(
                                                                index,
                                                                ColumnsFieldsNames.Width,
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </Flex>
                                                {isDataChart ? null : (
                                                    <Flex
                                                        justifyContent="center"
                                                        componentClasses={styles.tableItem}
                                                        {...createDataTestId(
                                                            'new-report-list-item-xasix',
                                                            true
                                                        )}
                                                    >
                                                        <SelectItem
                                                            disabled={!isTabular}
                                                            options={aXisModeData}
                                                            value={item.AxisMode}
                                                            onSelect={item =>
                                                                updateColumn(
                                                                    index,
                                                                    ColumnsFieldsNames.AxisMode,
                                                                    item.value
                                                                )
                                                            }
                                                        />
                                                    </Flex>
                                                )}
                                                <Flex
                                                    justifyContent="center"
                                                    componentClasses={styles.tableItem}
                                                    {...createDataTestId(
                                                        'new-report-list-item-showMine',
                                                        true
                                                    )}
                                                >
                                                    <CheckBox
                                                        componentClasses={styles.checkBox}
                                                        checked={item.ShowMine}
                                                        disabled={!item.ShowMineEnabled}
                                                        onChange={() =>
                                                            updateColumn(
                                                                index,
                                                                ColumnsFieldsNames.ShowMine,
                                                                !item.ShowMine
                                                            )
                                                        }
                                                    >
                                                        {item.Input}
                                                    </CheckBox>
                                                </Flex>
                                                <Flex
                                                    justifyContent="center"
                                                    componentClasses={styles.deleteIconContainer}
                                                >
                                                    <ModernTooltip
                                                        {...createDataTestId(
                                                            'new-report-list-item-hidden',
                                                            true
                                                        )}
                                                        tooltip={texts.hideEye}
                                                        position={isRTL() ? 'top-start' : 'top-end'}
                                                    >
                                                        <Icon
                                                            icon={
                                                                item.Hide
                                                                    ? PriorityIcon.passwordHide
                                                                    : PriorityIcon.showPasswordThin
                                                            }
                                                            componentClasses={
                                                                item.Hide
                                                                    ? styles.hideIcon
                                                                    : styles.hoverIcon
                                                            }
                                                            onClick={() =>
                                                                onToggleHidden(index, item)
                                                            }
                                                        />
                                                    </ModernTooltip>
                                                    <ModernTooltip
                                                        {...createDataTestId(
                                                            'new-report-list-item-trash',
                                                            true
                                                        )}
                                                        tooltip={texts.deleteField}
                                                        position={isRTL() ? 'top-start' : 'top-end'}
                                                    >
                                                        <Icon
                                                            componentClasses={styles.hoverIcon}
                                                            icon={PriorityIcon.delete}
                                                            onClick={() => handleDelete(index)}
                                                        />
                                                    </ModernTooltip>
                                                </Flex>
                                            </div>
                                        );
                                    }}
                                />
                            </div>
                        </div>
                    </Scrollbar>
                )}
            </div>
            {openFunctionDialog.isOpen && (
                <AutomationFunctionComponent
                    placeHolderText={texts.placeholderText}
                    defaultValue={openFunctionDialog.column?.Expr}
                    validateExpression={false}
                    helpText={texts.helpFunctionsDialog}
                    mainTitle={texts.titleFunctionsDialog}
                    optionsData={functionOptionsData}
                    session={currentScreen.session}
                    onClose={closeFunctionDialog}
                    onSave={onFunctionDialogSave}
                    onCriticalError={onCriticalError}
                />
            )}
        </>
    );
});
const SelectItem = ({options, value, onSelect, disabled = false}) => {
    const chooseOption = options.find(option => option.value === value);
    const rtl = isRTL();

    return (
        <ChooseDropdown
            disabled={disabled}
            portalZindex={10005}
            maxScrollHeight={MARGIN_OFFSET_TO_BOTTOM}
            hasSearch={false}
            isRtl={rtl}
            noOptionsText={texts.no_options_text}
            openingSide={rtl ? 'rightSide' : 'leftSide'}
            itemBorder={false}
            value={chooseOption?.value}
            onChange={onSelect}
            defaultFocusedIndex={0}
            trigger={
                <DropdownSelectTrigger
                    direction={rtl ? 'rtl' : 'ltr'}
                    value={chooseOption?.label}
                    disabled={disabled}
                />
            }
            data={options}
            searchPlaceholder={texts.search_placeholder}
            usePortal
            portalSelector={'menuholder'}
        />
    );
};
export default FieldTable;
